import {
  Button,
  Icon,
  List,
  Spacing,
  StartScreen,
  StyledDiv,
  Tab,
  Tabs,
  Typography,
} from '@taleemabad/dsm/components';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ChipFilterWithContainer } from '../../atoms/chip/chip-filter';
import styled from '@emotion/styled';
import { useWindowSize } from '@taleemabad/shared';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { IClassTimeTable, subjectIcons } from '@taleemabad/db';
import { Menu, menuItems } from '../../atoms/menu/menu';
import { TimetableContext } from '@taleemabad/providers';

export const FtueTimetableNewUser = 'ftue-timetable-new-user';
export const FtueTimetable = 'ftue-timetable';

export const shifts: Tab[] = [
  {
    key: 1,
    label: 'Morning',
    icon: 'light_mode',
  },
  {
    key: 2,
    label: 'Evening',
    icon: 'dark_mode',
  },
];
export interface Days {
  id: number;
  name: string;
  initials: string;
}

export interface AssignedClasses {
  id: number;
  schoolClass: number;
  label: string;
}

export interface TimetableAssignedSubjectProps {
  assignedClasses: IClassTimeTable[];
  days: Days[];
  menuData: menuItems[];
  onSelectedClass: (selectedClass: AssignedClasses) => void;
  onDaySelected: (id: number) => void;
  onShiftSelected: (id: number) => void;
  onAddClass: () => void;
  onFirstItem?: () => void;
  onTimetableEdit?: (timetableClass: IClassTimeTable) => void;
  onMenuClick?: (value: string, timetableClass: IClassTimeTable) => void;
  loadingComponent: () => any;
  header?: React.ReactNode;
}

export const TimetableAssignedSubjectScreen = ({
  assignedClasses,
  days,
  menuData,
  onSelectedClass,
  onShiftSelected,
  onDaySelected,
  onAddClass,
  onTimetableEdit,
  onMenuClick,
  loadingComponent,
  header,
}: TimetableAssignedSubjectProps) => {
  const { selectedDayItem, shiftItem, setSelectedDayItem, setShiftItem } =
    useContext(TimetableContext);

  const [width] = useWindowSize();

  const [openMenu, setOpenMenu] = useState<null | number>(null);
  const [assignClasses, setAssignClasses] = useState<IClassTimeTable[]>([]);
  useEffect(() => {
    const data =
      assignedClasses?.filter(
        (item) =>
          item?.daysOfWeek?.includes(selectedDayItem?.id as number) &&
          item.shift === shiftItem,
      ) ?? [];
    setAssignClasses(data);
  }, [selectedDayItem, assignedClasses, shiftItem]);
  useEffect(() => {
    if (!selectedDayItem) {
      setSelectedDayItem(getSelectedDay(days) || days[0]);
    }
    if (!shiftItem) {
      setShiftItem(shifts[0].label.toUpperCase());
    }
  }, []);

  const initialShowStartScreen = useMemo(() => {
    return (
      ((assignedClasses &&
        assignedClasses.length === 0 &&
        localStorage.getItem('FtueTimetableNewUser')) ||
        'null') === null
    );
  }, [assignedClasses]);
  const [showStartScreen, setShowStartScreen] = useState(
    initialShowStartScreen,
  );
  const handleToggleMenu = (classId: number) => {
    setOpenMenu((prevOpenMenu) => (prevOpenMenu === classId ? null : classId));
  };
  useEffect(() => {
    const time = setTimeout(() => {
      setShowStartScreen(false);
    }, 2500);
    return () => clearTimeout(time);
  }, []);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      setOpenMenu(null);
    };
    if (listRef.current && openMenu) {
      listRef.current.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (listRef.current && openMenu) {
        listRef.current.addEventListener('scroll', handleScroll);
      }
    };
  }, []);
  const groupedClasses = useMemo(() => {
    return assignClasses.reduce(
      (acc, curr) => {
        const grade = curr.grade ?? 0;
        if (!acc[grade]) {
          acc[grade] = [];
        }
        acc[grade].push(curr);
        return acc;
      },
      {} as Record<number, IClassTimeTable[]>,
    );
  }, [assignClasses]);

  return (
    <>
      {showStartScreen && <StartScreen />}
      {header && <Spacing marginTop="xl">{header}</Spacing>}
      <StyledDiv gap={8} marginTop={32} marginBottom={24}>
        <Icon name="today" />
        <Typography variant="textTitleLarge">Session: 2025-2026</Typography>
      </StyledDiv>
      <Spacing marginBottom="xxl">
        <Tabs
          variant="secondary"
          activeTabKey={
            (shifts.find((data) => data.label.toUpperCase() === shiftItem)
              ?.key as number) || 1
          }
          tabs={shifts}
          data-cy="timetable-shift-selector"
          onChange={(value: number) => {
            onShiftSelected(value);
            setSelectedDayItem(days[0]);
            setShiftItem(
              shifts
                .find((data) => data.key === value)
                ?.label.toLocaleUpperCase() as string,
            );
          }}
        />
      </Spacing>
      <StyledSelectedDaysContainer>
        {days.map((item) => (
          <ChipFilterWithContainer
            selected={selectedDayItem === item}
            onClick={() => {
              setSelectedDayItem(item);
              onDaySelected(item.id);
            }}
            key={item.name}
            data-cy={`timetable-day-${item.name.toLowerCase()}`}
            label={width < 575 ? item.initials : item.name}
          />
        ))}
      </StyledSelectedDaysContainer>
      <StyledCardContainer ref={listRef}>
        {Object.entries(groupedClasses)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([gradeNum, classes]) => (
            <StyledDiv flexDirection="column" gap={12} key={gradeNum}>
              <Typography variant="textTitleMedium">
                {classes[0]?.gradeLabel ?? `Grade ${gradeNum}`}
              </Typography>
              <StyledClassList>
                {classes
                  .sort((a, b) => b.lastLocalModifiedAt - a.lastLocalModifiedAt)
                  .map((assignedClass, index) => (
                    <Spacing
                      key={
                        assignedClass.gradeSubject +
                        assignedClass.gradeLabel +
                        assignedClass.section +
                        assignedClass.shift +
                        index +
                        selectedDayItem?.name
                      }
                      style={{ position: 'relative' }}
                      marginBottom="md"
                    >
                      <List
                        alignment="center"
                        heading={`${assignedClass.subjectLabel}`}
                        supportingText={
                          assignedClass.deletionBlocked
                            ? `${assignedClass.gradeLabel} - ${assignedClass.section} · Has observations`
                            : ` ${assignedClass.gradeLabel} - ${assignedClass.section}`
                        }
                        data-cy="timetable-assigned-classes"
                        listContainerStyle={
                          assignedClass.deletionBlocked ? 'error' : 'outline'
                        }
                        leadingIcon={{
                          direction: 'leading',
                          source:
                            subjectIcons.find(
                              (icon) => icon.id === assignedClass.subject,
                            )?.icon ?? '',
                          name: assignedClass.subjectLabel as string,
                          type: 'image',
                        }}
                        trailingIcon={
                          assignedClass.deletionBlocked
                            ? {
                              direction: 'trailing',
                              source: 'warning',
                              name: 'deletion blocked',
                              type: 'icon',
                            }
                            : {
                              direction: 'trailing',
                              source: 'more_vert',
                              name: 'more_vert',
                              type: 'icon',
                            }
                        }
                        onClick={() => {
                          if (assignedClass.id && !openMenu) {
                            onSelectedClass?.({
                              id: assignedClass.gradeSubject,
                              schoolClass: assignedClass.schoolClass,
                              label: assignedClass.gradeLabel,
                            });
                          }
                        }}
                        onTrailingIconClick={() =>
                          handleToggleMenu(assignedClass.id as number)
                        }
                      />
                      <Menu
                        positioning="absolute"
                        xOffset={40}
                        yOffset={-13}
                        open={openMenu === assignedClass.id}
                        menuCorner="end-end"
                        anchorCorner="end-end"
                        menuItems={menuData}
                        onSelectedItem={(data) => {
                          setOpenMenu(null);
                          onMenuClick?.(
                            data.value ?? '',
                            assignedClass as IClassTimeTable,
                          );
                          if (data.id === 1) {
                            onTimetableEdit?.(assignedClass as IClassTimeTable);
                          }
                        }}
                        onClose={() => {
                          setOpenMenu(null);
                        }}
                      />
                    </Spacing>
                  ))}
              </StyledClassList>
            </StyledDiv>
          ))}
        {loadingComponent()}
      </StyledCardContainer>

      <StyledAddClassButton>
        <Button
          onClick={onAddClass}
          variant="filled"
          children="+ Add Class"
          data-cy="timetable-add-class-btn"
        />
      </StyledAddClassButton>
    </>
  );
};

const StyledSelectedDaysContainer = styled.div`
  display: flex;
  inline-flex: 1;
  flex-direction: row;
  justify-content: center;
  gap: var(--rk-spacing-lg);
`;
const StyledCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-xl);
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100vh - 324px);
  padding-top: var(--rk-spacing-xl);
  padding-bottom: var(--rk-spacing-xl);

  ::-webkit-scrollbar {
    width: var(--rk-spacing-lg);
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--md-sys-color-on-primary-container);
    border-radius: var(--rk-spacing-lg);
    border: var(--rk-spacing-xs) solid var(--md-sys-color-on-primary);
  }
`;
const StyledClassList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: 1fr;
  }
`;

const StyledAddClassButton = styled.div`
  position: absolute;
  bottom: 50px;
  left: var(--rk-spacing-xxxxxxxl) * 2;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - (var(--rk-spacing-xxxxxxxl) * 5));
  overflow: hidden;
  ${mq(BreakpointSizes.xs)} {
    left: var(--rk-spacing-xl);
    width: calc(100% - var(--rk-spacing-xl));
    bottom: 78px;
    height: 78px;
    align-content: center;
    background-color: var(--md-sys-color-on-primary);
    z-index: 0;
  }
`;

export const getSelectedDay = (days: Days[]) => {
  const currentDate = new Date();
  let currentDayIndex = currentDate.getDay();
  if (currentDayIndex === 0) {
    currentDayIndex = 1;
  }
  const currentDay = days.find((day) => day.id === currentDayIndex);
  return currentDay;
};
